import { computed, inject, Injectable } from '@angular/core';
import { createInfiniteScroll, type InfiniteScrollRefreshSource } from '@okcargo-apps/layout-ionic';
import { IUser } from '@okcargo-apps/shared-domain';
import { CRUD_USERS_CAPABILITIES } from '@okcargo-apps/shared-domain-ionic';
import { UserQueryService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account';
import { manageableRolesFor } from './user-role.utils';

@Injectable({
  providedIn: 'root',
})
export class UsersStoreService {
  private readonly userQueryService = inject(UserQueryService);
  private readonly accountService = inject(AccountService);

  private readonly PAGE_SIZE = 200;
  private readonly RELOAD_DELAY_MS = 2000;

  /** Users are only loaded when the active user can manage them. */
  private readonly canViewUsers = computed(() =>
    CRUD_USERS_CAPABILITIES.some((capability) => this.accountService.hasCapability(capability)),
  );

  // The backend does not return pagination metadata, so totalElements is hydrated by counting the
  // returned items. Ready to switch to real pagination once the query provides it.
  private readonly usersCollection = createInfiniteScroll<{ list: string }, IUser>({
    query: computed(() => (this.canViewUsers() ? { list: 'users' } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: async (_query, pageIndex, pageSize, abortSignal) => {
      const items = await this.userQueryService.searchUsers({ pageIndex, pageSize, abortSignal });
      return { items, totalElements: items.length };
    },
    getItemKey: (user) => user.id,
  });

  public readonly usersVM = this.usersCollection.vm;

  /** Roles the active user is allowed to assign, derived only from its capabilities. */
  public readonly manageableRoles = computed(() => manageableRolesFor(this.accountService.account().capabilities));

  public loadNextUsersPage(): void {
    this.usersCollection.loadNextPage();
  }

  public retryUsers(): void {
    this.usersCollection.retry();
  }

  public reloadUsers(source: InfiniteScrollRefreshSource = 'polling'): void {
    this.usersCollection.refresh(source);
  }

  /**
   * Consumer components (the users list, the assignation modal) reload the users when they are
   * initialized. This query is not refreshed by the general application refresh: it loads once
   * on application start (declaratively, keeping data for offline mode) and then on each
   * consumer component initialization.
   */
  public reloadUsersOnComponentInit(): void {
    this.reloadUsers('user');
  }

  /** Reloads the users after a short delay, once the backend has processed the command. */
  public async reloadAfterSaveData(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, this.RELOAD_DELAY_MS));
    this.reloadUsers('user');
  }
}
